<template>
  <div class="rc-carousel-hooper--body">
    <div
      v-if="isNavButtonVisible"
      class="rc-carousel-hooper--controls"
    >
      <rc-btn
        variant="link"
        class="rc-btn-icon"
        aria-hidden="true"
        tabindex="-1"
        :disabled="isPrevDisabled"
        @click="slidePrev"
      >
        <RcIcon
          name="chevronLeft"
          scale="1"
          :title="$t('nav.previous')"
        />
      </rc-btn>
      <rc-btn
        variant="link"
        class="rc-btn-icon"
        aria-hidden="true"
        tabindex="-1"
        :disabled="isNextDisabled"
        @click="slideNext"
      >
        <RcIcon
          name="chevronRight"
          scale="1"
          :title="$t('nav.next')"
        />
      </rc-btn>
    </div>
    <div class="rc-carousel-hooper--slides">
      <div class="row g-0">
        <button
          v-if="isNavButtonVisible"
          ref="btnSkipToEnd"
          type="button"
          class="btn btn-white btn-sm rc-btn-skip-to-end"
          @click="slideEnd"
        >
          {{ results.length }} items <span aria-hidden="true">||</span>
          {{ $t("a11y.nav.go_to_end") }}
        </button>
        <Hooper
          ref="carousel"
          :settings="mergedSettings"
          @slide="onSlide"
          @updated="onUpdated"
        >
          <Slide
            v-for="item in results"
            :key="item.uid"
          >
            <div @click="onUnitClicked">
              <component
                :is="unitType"
                class="col"
                :variant="variant"
                :isStacked="isStacked"
                :locked="locked"
                :item="item"
                @keydown-tab="slideNext"
                @keydown-shift-tab="slidePrev"
              />
            </div>
          </Slide>
        </Hooper>
        <button
          v-if="isNavButtonVisible"
          ref="btnSkipToStart"
          class="btn btn-white btn-sm rc-btn-skip-to-start"
          @click="slideStart"
        >
          {{ results.length }} items <span aria-hidden="true">||</span>
          {{ $t("a11y.nav.go_to_start") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import RcGridUnit from '@/libvue/apps/catalog/items/GridUnit.vue';
import RcTileUnit from '@/libvue/apps/catalog/items/TileUnit.vue';
import RcCardUnit from '@/libvue/apps/catalog/items/CardUnit.vue';
import RcGridCategoryUnit from '@/libvue/apps/catalog/items/GridCategoryUnit.vue';
import { Hooper, Slide } from 'hooper-vue3';
import RcIcon from '@/libvue/components/icons/Base.vue';
import { nextTick } from 'vue';
import 'hooper-vue3/dist/hooper.css';

import { SET_UP_NEXT_LIST } from '@/libvue/apps/catalog/items/actions';

export default {
  name: 'RcCarousel',
  components: {
    RcIcon,
    RcGridUnit,
    RcTileUnit,
    RcCardUnit,
    RcGridCategoryUnit,
    Hooper,
    Slide
  },
  props: {
    items: {
      type: Object,
      default: () => {}
    },
    locked: {
      type: Boolean,
      default: false
    },
    omitUID: {
      type: String,
      required: false,
      default: null
    },
    unitType: {
      type: String,
      required: false,
      default: 'RcGridUnit'
    },
    //item, theme
    variant: {
      type: String,
      default: 'item'
    },
    isStacked: {
      type: Boolean,
      default: false
    },
    upNext: {
      type: Boolean,
      required: false,
      default: true
    },

    settings: {
      type: Object,
      default: () => ({
        rtl: false,
        infiniteScroll: false,
        wheelControl: false,
        trimWhiteSpace: true,
        keysControl: false,
        initialSlide: 0,
        breakpoints: {
          1200: {
            itemsToShow: 4.57
          },
          992: {
            itemsToShow: 3.7
          },
          768: {
            itemsToShow: 3.5
          },
          576: {
            itemsToShow: 2.25
          },
          0: {
            itemsToShow: 1.2
          }
        }
      })
    }
  },
  data() {
    return {
      hooperSettings: {
        rtl: false,
        infiniteScroll: false,
        wheelControl: false,
        trimWhiteSpace: true,
        keysControl: false,
        breakpoints: {
          1200: {
            itemsToShow: 4.57
          },
          992: {
            itemsToShow: 3.7
          },
          768: {
            itemsToShow: 3.5
          },
          576: {
            itemsToShow: 2.25
          },
          0: {
            itemsToShow: 1.2
          }
        }
      },
      isPrevDisabled: true,
      isNextDisabled: false,
      isNavButtonVisible: true
    };
  },
  computed: {
    results() {
      return this.omitUID
        ? this.items?.data?.results?.filter((i) => i.uid !== this.omitUID)
        : this.items?.data?.results;
    },
    mergedSettings() {
      return {
        ...this.hooperSettings,
        ...this.settings
      };
    }
  },

  watch: {
    results() {
      this.onUpdated({
        settings: { itemsToShow: this.$refs.carousel.config.itemsToShow }
      });
    }
  },

  methods: {
    onUnitClicked() {
      this.upNext
        ? this.$store.commit(SET_UP_NEXT_LIST, this.results)
        : this.$store.commit(SET_UP_NEXT_LIST, []);
    },
    slidePrev() {
      this.$refs.carousel.slidePrev();
    },
    slideNext() {
      this.$refs.carousel.slideNext();
    },
    slideStart() {
      this.$refs.carousel.slideTo(0);
      this.$refs.btnSkipToEnd.focus();
    },
    slideEnd() {
      this.$refs.carousel.slideTo(this.results.length);
      this.$refs.btnSkipToStart.focus();
    },
    onUpdated(e) {
      if (this.results.length < e.settings.itemsToShow) {
        this.isNavButtonVisible = false;
      } else {
        this.isNavButtonVisible = true;
      }

      nextTick(() => {
        this.$refs.carousel.$el.removeAttribute('tabindex');

        let lis = this.$refs.carousel.$el.querySelectorAll(
          'li.hooper-slide:not(.is-active)'
        );
        lis.forEach(function (li) {
          li.removeAttribute('aria-hidden');
        });
      });
    },

    onSlide(e) {
      //isPrevDisabled
      if (e.currentSlide != 0) {
        this.isPrevDisabled = false;
      } else {
        this.isPrevDisabled = true;
        this.$refs.carousel.$el.classList.remove('rc-hooper-end');
      }
      //isNextDisabled
      const itemsHidden =
        this.results.length - this.$refs.carousel.config.itemsToShow;

      if (itemsHidden == e.currentSlide) {
        this.isNextDisabled = true;
        this.$refs.carousel.$el.classList.add('rc-hooper-end');
      } else {
        this.isNextDisabled = false;
      }
    }
  }
};
</script>

<style lang="scss">
//test pour première slide plus large
// li.hooper-slide {
//   transition: width 0.3s ease-in;
// }
// li.is-current {
//   width: calc(50% - 5rem - 20px) !important;
// }
</style>
